import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import { makeStyles } from '@material-ui/core/styles';
import AccountBalanceWalletOutlinedIcon from '@material-ui/icons/AccountBalanceWalletOutlined';
import MonetizationOnOutlinedIcon from '@material-ui/icons/MonetizationOnOutlined';
import TouchAppIcon from '@material-ui/icons/TouchApp';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import GoogleCampaignSpendTable from '../../components/marketing/GoogleCampaignSpendTable';
import MetaAdsCampaignLoader from '../../components/marketing/MetaAdsCampaignLoader';
import MetaAdsLeadDialog from '../../components/marketing/MetaAdsLeadDialog';
import MarketingSpendOverviewAccordion from '../../components/marketing/MarketingSpendOverviewAccordion';
import SpendTrendChart from '../../components/marketing/SpendTrendChart';
import {
    combinedCampaignInScope,
    combinedKnownCities,
    combinedScopeDaily,
    googleRecordInScope,
    unifiedCityTags,
} from '../../components/marketing/combinedAdsScope';
import {
    mergeCityTargets,
    mergeUnifiedCampaigns,
    primaryCityForUnified,
    splitPrefixedCampaignIds,
} from '../../components/marketing/mergeChannelCampaigns';
import {
    combinedReportCacheKey,
    isMarketingAdsEntryFresh,
    metaCityDailyCacheKey,
} from '../../components/marketing/marketingAdsCacheKey';
import {
    selectGoogleEntry,
    selectGoogleTargets,
    selectMetaEntry,
    selectMetaTargets,
} from '../../components/marketing/marketingAdsSelectors';
import { ZONES, citiesMatchingZones, zoneForCity } from '../../components/marketing/cityZones';
import {
    setMarketingAdsFilters,
    setMarketingAdsGoogle,
    setMarketingAdsMeta,
} from '../../redux/actions/marketingAdsAction';
import { formatMetric, formatMoney, formatNumber, localDate } from '../../components/marketing/format';
import {
    getAdCities,
    getCampaignReport,
    getCityDailyBreakdown,
    getLeadTargets,
    getMetaAdsLeadDetails,
    getSpendTrend,
    isCanceled,
    normalizeCampaigns,
    normalizeDaily,
} from '../../services/metaAdsService';
import {
    getGoogleAdsLeadDetails,
    getGoogleAdsLeadTargets,
    getGoogleAdsReport,
    isCanceled as isGoogleCanceled,
} from '../../services/googleAdsService';
import LeadChannelSwitch from './LeadChannelSwitch';

const LEAD_BUCKET_LABEL = {
    received: 'Leads received',
    sql: 'SQL-Real',
    tbc: 'Sales-TBC',
    real: 'Total Real',
    won: 'Sales Won',
};

const useStyles = makeStyles(() => ({
    root: { backgroundColor: '#f3f5f8', minHeight: '100%', padding: '4px 4px 24px' },
    page: { maxWidth: 1440, margin: '0 auto' },
    headerCard: {
        backgroundColor: '#fff',
        borderRadius: 12,
        padding: '20px 24px',
        marginBottom: 16,
        boxShadow: '0 1px 3px rgba(16, 24, 40, 0.08)',
    },
    title: { fontSize: 24, fontWeight: 700, color: '#1C375D', margin: 0 },
    subtitle: { fontSize: 13, color: '#8a92a3', marginTop: 6 },
    filters: {
        display: 'flex',
        gap: 16,
        flexWrap: 'wrap',
        alignItems: 'center',
        backgroundColor: '#fff',
        borderRadius: 12,
        padding: '16px 20px',
        marginBottom: 16,
        boxShadow: '0 1px 3px rgba(16, 24, 40, 0.08)',
    },
    cityField: { minWidth: 220 },
    zoneField: { minWidth: 220 },
    loaderCard: {
        backgroundColor: '#fff',
        borderRadius: 12,
        marginBottom: 16,
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 16,
        marginBottom: 8,
    },
    card: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        backgroundColor: '#fff',
        borderRadius: 12,
        padding: '16px 18px',
        boxShadow: '0 1px 3px rgba(16, 24, 40, 0.08)',
        minWidth: 0,
    },
    icon: {
        width: 36,
        height: 36,
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#1C375D14',
        color: '#1C375D',
        flexShrink: 0,
    },
    label: {
        fontSize: 11.5,
        color: '#8a92a3',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
    },
    value: {
        fontSize: 20,
        fontWeight: 700,
        color: '#1C375D',
        marginTop: 2,
        overflowWrap: 'anywhere',
    },
    footnote: {
        margin: '8px 0 16px',
        color: '#8a92a3',
        fontSize: 12,
    },
}));

function SummaryCard({ classes, icon, label, value, onClick }) {
    const clickable = typeof onClick === 'function';
    return (
        <article
            className={classes.card}
            onClick={clickable ? onClick : undefined}
            onKeyDown={clickable ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onClick();
                }
            } : undefined}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            style={clickable ? { cursor: 'pointer' } : undefined}
        >
            <div className={classes.icon}>{icon}</div>
            <div style={{ minWidth: 0 }}>
                <div className={classes.label}>{label}</div>
                <div className={classes.value}>{value}</div>
            </div>
        </article>
    );
}

function sameCurrency(campaigns) {
    const codes = [...new Set((campaigns || []).map((campaign) => campaign.currency || 'INR'))];
    return {
        mixed: codes.length > 1,
        currency: codes[0] || 'INR',
    };
}

export default function CombinedAdsDashboard({ channel = 'both', onChannel }) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const savedFilters = useSelector((state) => state.marketingAds?.filters);
    const metaTargetsState = useSelector(selectMetaTargets);
    const googleTargetsState = useSelector(selectGoogleTargets);
    const today = localDate();
    const monthStart = `${today.slice(0, 7)}-01`;
    const mounted = useRef(true);
    const [from, setFrom] = useState(() => savedFilters?.from || monthStart);
    const [to, setTo] = useState(() => savedFilters?.to || today);
    const [city, setCity] = useState('all');
    const [zones, setZones] = useState([]);
    const [masterCities, setMasterCities] = useState([]);
    const metaPlatform = 'both';
    const [metaCampaigns, setMetaCampaigns] = useState([]);
    const [googleCampaigns, setGoogleCampaigns] = useState([]);
    const [googleRecords, setGoogleRecords] = useState([]);
    const [metaCityDaily, setMetaCityDaily] = useState(null);
    const [dailyLoading, setDailyLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [leadDialog, setLeadDialog] = useState(null);
    const [leadPicker, setLeadPicker] = useState(null);

    // Both has no snapshot of its own: it reads the Meta (platform=both)
    // and Google entries for this range. A sync/refresh on either channel
    // tab therefore updates Both, and loading Both warms those tabs.
    const reportCacheKey = combinedReportCacheKey(from, to);
    const metaEntry = useSelector((state) => selectMetaEntry(state, from, to, 'both'));
    const googleEntry = useSelector((state) => selectGoogleEntry(state, from, to));
    const hasMetaCache = Boolean(metaEntry?.lastUpdated);
    const hasGoogleCache = Boolean(googleEntry?.lastUpdated);
    const hasCachedReport = hasMetaCache && hasGoogleCache;
    const cachesAreFresh = hasCachedReport
        && isMarketingAdsEntryFresh(metaEntry, to)
        && isMarketingAdsEntryFresh(googleEntry, to);
    const hasData = hasMetaCache || hasGoogleCache || metaCampaigns.length > 0 || googleCampaigns.length > 0;
    const targets = useMemo(
        () => mergeCityTargets(metaTargetsState, googleTargetsState),
        [metaTargetsState, googleTargetsState],
    );

    useEffect(() => () => {
        mounted.current = false;
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        getAdCities({ signal: controller.signal })
            .then((names) => {
                if (!controller.signal.aborted && mounted.current) setMasterCities(names);
            })
            .catch(() => {});
        return () => controller.abort();
    }, []);

    const load = useCallback(async (signal, { background = false } = {}) => {
        if (!from || !to || from > to) return;
        const needMeta = !hasMetaCache || !isMarketingAdsEntryFresh(metaEntry, to);
        const needGoogle = !hasGoogleCache || !isMarketingAdsEntryFresh(googleEntry, to);
        if (!needMeta && !needGoogle) return;
        if (!background) setLoading(true);
        setError('');
        try {
            let metaTargetsOk = false;
            let googleTargetsOk = false;
            const [metaReport, googleReport, metaTrend, nextMetaTargets, nextGoogleTargets] = await Promise.all([
                needMeta ? getCampaignReport({ from, to, platform: metaPlatform, signal }) : Promise.resolve(null),
                needGoogle ? getGoogleAdsReport({ startDate: from, endDate: to, signal }) : Promise.resolve(null),
                needMeta ? getSpendTrend({ from, to, platform: metaPlatform, signal }).catch(() => null) : Promise.resolve(null),
                needMeta
                    ? getLeadTargets({ signal }).then((value) => { metaTargetsOk = true; return value; }).catch(() => ({}))
                    : Promise.resolve(null),
                needGoogle
                    ? getGoogleAdsLeadTargets({ signal }).then((value) => { googleTargetsOk = true; return value; }).catch(() => ({}))
                    : Promise.resolve(null),
            ]);
            if (signal?.aborted || !mounted.current) return;

            if (needMeta && metaReport) {
                const nextMeta = normalizeCampaigns(metaReport);
                const metaIds = nextMeta.map((c) => c.campaignId).filter(Boolean).sort().join(',');
                let nextMetaCityDaily = metaEntry?.metaCityDaily || null;
                if (metaIds) {
                    try {
                        nextMetaCityDaily = await getCityDailyBreakdown({
                            from,
                            to,
                            campaignIds: metaIds,
                            platform: metaPlatform,
                            signal,
                        });
                    } catch (cityDailyError) {
                        if (!isCanceled(cityDailyError) && !signal?.aborted) nextMetaCityDaily = null;
                    }
                }
                if (signal?.aborted || !mounted.current) return;
                setMetaCampaigns(nextMeta);
                setMetaCityDaily(nextMetaCityDaily);
                dispatch(setMarketingAdsMeta({
                    cacheKey: `${from}|${to}|${metaPlatform}`,
                    campaigns: nextMeta,
                    daily: metaTrend ? normalizeDaily(metaTrend) : (metaEntry?.daily || []),
                    metaCityDaily: nextMetaCityDaily,
                    cityDailyCacheKey: metaCityDailyCacheKey(from, to, metaPlatform, metaIds),
                    ...(metaTargetsOk ? { targets: nextMetaTargets } : {}),
                }));
            }
            if (needGoogle && googleReport) {
                const nextGoogle = googleReport.campaigns || [];
                const nextRecords = googleReport.records || [];
                setGoogleCampaigns(nextGoogle);
                setGoogleRecords(nextRecords);
                dispatch(setMarketingAdsGoogle({
                    cacheKey: reportCacheKey,
                    campaigns: nextGoogle,
                    records: nextRecords,
                    daily: googleReport.daily || [],
                    ...(googleTargetsOk ? { targets: nextGoogleTargets } : {}),
                }));
            }
            dispatch(setMarketingAdsFilters({ from, to, metaPlatform }));
        } catch (e) {
            if (isCanceled(e) || isGoogleCanceled(e) || signal?.aborted) return;
            if (!background) {
                setMetaCampaigns([]);
                setGoogleCampaigns([]);
            }
            setError('Unable to load combined Meta + Google report.');
        } finally {
            if (!signal?.aborted && mounted.current) setLoading(false);
        }
    }, [from, to, metaPlatform, reportCacheKey, dispatch, hasMetaCache, hasGoogleCache, metaEntry, googleEntry]);

    useEffect(() => {
        dispatch(setMarketingAdsFilters({ from, to, metaPlatform, channel: 'both' }));
    }, [dispatch, from, to, metaPlatform]);

    useEffect(() => {
        if (!metaEntry) return;
        setMetaCampaigns(metaEntry.campaigns || []);
        setMetaCityDaily(metaEntry.metaCityDaily || null);
        setLoading(false);
    }, [metaEntry]);

    useEffect(() => {
        if (!googleEntry) return;
        setGoogleCampaigns(googleEntry.campaigns || []);
        setGoogleRecords(googleEntry.records || []);
        setLoading(false);
    }, [googleEntry]);

    useEffect(() => {
        // Both channels fresh: cache only. Otherwise show what is cached
        // instantly and refresh only the missing/stale channel(s).
        if (cachesAreFresh || !from || !to || from > to) return undefined;
        const controller = new AbortController();
        load(controller.signal, { background: hasMetaCache || hasGoogleCache });
        return () => controller.abort();
    }, [cachesAreFresh, hasMetaCache, hasGoogleCache, from, to, load]);

    const unified = useMemo(
        () => mergeUnifiedCampaigns(metaCampaigns, googleCampaigns),
        [metaCampaigns, googleCampaigns],
    );

    const knownCities = useMemo(
        () => combinedKnownCities(unified, masterCities),
        [unified, masterCities],
    );

    const zoneCityOptions = useMemo(
        () => citiesMatchingZones(knownCities, zones),
        [knownCities, zones],
    );

    useEffect(() => {
        if (city === 'all' || city === '__untagged') return;
        if (zones.length && !zones.includes(zoneForCity(city))) setCity('all');
    }, [city, zones]);

    const scope = useMemo(() => ({ city, zones }), [city, zones]);

    const visibleCampaigns = useMemo(
        () => unified.filter((campaign) => combinedCampaignInScope(campaign, scope)),
        [unified, scope],
    );

    const visibleGoogleIds = useMemo(() => new Set(
        visibleCampaigns
            .filter((campaign) => campaign.channel === 'google')
            .map((campaign) => String(campaign.reportingCampaignId || campaign.campaignId).replace(/^google:/, '')),
    ), [visibleCampaigns]);

    const visibleRecords = useMemo(
        () => (googleRecords || []).filter((row) => {
            if (!visibleGoogleIds.has(String(row.campaignId))) return false;
            return googleRecordInScope(row, scope);
        }),
        [googleRecords, visibleGoogleIds, scope],
    );

    const visibleDaily = useMemo(
        () => combinedScopeDaily({
            metaCityDaily,
            records: googleRecords,
            visibleCampaigns,
            city,
            zones,
        }),
        [metaCityDaily, googleRecords, visibleCampaigns, city, zones],
    );

    const hasUntagged = useMemo(
        () => unified.some((campaign) => unifiedCityTags(campaign).includes('Untagged')),
        [unified],
    );

    const cityLabel = city === '__untagged' ? 'Untagged' : city;
    const scopeLabel = useMemo(() => {
        if (city !== 'all') return cityLabel;
        if (zones.length) {
            const names = citiesMatchingZones(knownCities, zones);
            return names.length ? `${zones.join(', ')} (${names.join(', ')})` : zones.join(', ');
        }
        return 'All cities';
    }, [city, cityLabel, zones, knownCities]);

    const { currency, mixed } = useMemo(
        () => sameCurrency(visibleCampaigns),
        [visibleCampaigns],
    );

    const totals = useMemo(() => {
        const spendFromDaily = visibleDaily.reduce((sum, row) => sum + (row.spend || 0), 0);
        const spendFromCampaigns = visibleCampaigns.reduce((sum, campaign) => sum + (campaign.spend || 0), 0);
        const spend = visibleDaily.length ? spendFromDaily : spendFromCampaigns;
        const clicks = visibleDaily.length
            ? visibleDaily.reduce((sum, row) => sum + (row.clicks || 0), 0)
            : visibleCampaigns.reduce((sum, campaign) => sum + (campaign.clicks || 0), 0);
        const impressions = visibleDaily.length
            ? visibleDaily.reduce((sum, row) => sum + (row.impressions || 0), 0)
            : visibleCampaigns.reduce((sum, campaign) => sum + (campaign.impressions || 0), 0);
        const leadCount = visibleCampaigns.reduce((sum, campaign) => sum + (campaign.leadCount || 0), 0);
        const sqlReal = visibleCampaigns.reduce((sum, campaign) => sum + (campaign.sqlReal || 0), 0);
        const salesTbc = visibleCampaigns.reduce((sum, campaign) => sum + (campaign.salesTbc || 0), 0);
        const totalReal = visibleCampaigns.reduce((sum, campaign) => sum + (campaign.totalReal || 0), 0);
        return {
            spend,
            clicks,
            impressions,
            cpc: !mixed && clicks > 0 ? spend / clicks : null,
            leadCount,
            sqlReal,
            salesTbc,
            totalReal,
            costPerReal: !mixed && totalReal > 0 ? spend / totalReal : null,
        };
    }, [visibleCampaigns, visibleDaily, mixed]);

    useEffect(() => {
        setDailyLoading(false);
    }, [hasCachedReport, metaCityDaily]);

    function openChannelLeads(source, ids, bucket, label, day) {
        if (!ids.length) return;
        if (source === 'google') {
            setLeadDialog({
                title: `${LEAD_BUCKET_LABEL[bucket] || 'Leads'} · ${label}`,
                query: {
                    startDate: day || from,
                    endDate: day || to,
                    bucket,
                    campaignIds: ids.join(','),
                },
                loader: getGoogleAdsLeadDetails,
                dateLabel: 'Form date',
            });
        } else {
            setLeadDialog({
                title: `${LEAD_BUCKET_LABEL[bucket] || 'Leads'} · ${label}`,
                query: {
                    from: day || from,
                    to: day || to,
                    bucket,
                    campaignIds: ids.join(','),
                    ...(metaPlatform !== 'both' ? { platform: metaPlatform } : {}),
                },
                loader: getMetaAdsLeadDetails,
                dateLabel: 'Meta created date',
            });
        }
    }

    function openLeads(bucket, campaignIds, label, day) {
        const { meta, google } = splitPrefixedCampaignIds(campaignIds);
        if (meta.length && google.length) {
            setLeadPicker({ bucket, label, day, meta, google });
            return;
        }
        if (google.length) openChannelLeads('google', google, bucket, label, day);
        else openChannelLeads('meta', meta, bucket, label, day);
    }

    function openScopeLeads(bucket, day) {
        openLeads(
            bucket,
            visibleCampaigns.map((campaign) => campaign.campaignId),
            scopeLabel,
            day,
        );
    }

    const money = (value) => {
        if (!hasData || mixed) return '—';
        return formatMetric(value, (amount) => formatMoney(amount, currency));
    };

    return (
        <section className={classes.root}>
            <div className={classes.page}>
                <header className={classes.headerCard}>
                    <LeadChannelSwitch value={channel} onChange={onChannel} />
                    <h1 className={classes.title}>Meta + Google (by city)</h1>
                    <p className={classes.subtitle}>
                        Combined city breakdown: Meta (Facebook + Instagram) and Google campaigns together.
                        {' '}Use zone and city filters like the Meta and Google tabs. Refresh data with Sync on those tabs.
                    </p>
                </header>
                <div className={classes.filters}>
                    <TextField
                        label="From"
                        type="date"
                        size="small"
                        variant="outlined"
                        value={from}
                        inputProps={{ max: to < today ? to : today }}
                        onChange={(e) => setFrom(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        label="To"
                        type="date"
                        size="small"
                        variant="outlined"
                        value={to}
                        inputProps={{ min: from, max: today }}
                        onChange={(e) => setTo(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <FormControl className={classes.zoneField} variant="outlined" size="small">
                        <InputLabel id="combined-ads-zone-label">Zone</InputLabel>
                        <Select
                            labelId="combined-ads-zone-label"
                            multiple
                            value={zones}
                            onChange={(event) => setZones(event.target.value)}
                            label="Zone"
                            renderValue={(selected) => (selected.length ? selected.join(', ') : 'All zones')}
                        >
                            {ZONES.map((name) => (
                                <MenuItem key={name} value={name}>
                                    <Checkbox checked={zones.indexOf(name) > -1} color="primary" />
                                    <ListItemText primary={name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        className={classes.cityField}
                        select
                        label="City"
                        size="small"
                        variant="outlined"
                        value={city}
                        onChange={(event) => setCity(event.target.value)}
                    >
                        <MenuItem value="all">All cities</MenuItem>
                        {zoneCityOptions.map((name) => (
                            <MenuItem key={name} value={name}>{name}</MenuItem>
                        ))}
                        {hasUntagged && <MenuItem value="__untagged">Untagged</MenuItem>}
                    </TextField>
                </div>
                {error && <p style={{ color: '#a61b1b' }} role="alert">{error}</p>}
                {loading && !hasCachedReport ? (
                    <div className={classes.loaderCard}>
                        <MetaAdsCampaignLoader active phase="campaigns" />
                    </div>
                ) : (
                    <>
                        <MarketingSpendOverviewAccordion
                            title="Spend summary & daily chart (Meta + Google)"
                            hint={mixed ? 'Multiple currencies' : `Spend ${formatMoney(totals.spend, currency)} · Total Real ${formatNumber(totals.totalReal)}`}
                        >
                            <div className={classes.grid}>
                                <SummaryCard
                                    classes={classes}
                                    icon={<AccountBalanceWalletOutlinedIcon fontSize="small" />}
                                    label="Total spend"
                                    value={mixed ? 'Multiple currencies' : formatMoney(totals.spend, currency)}
                                />
                                <SummaryCard
                                    classes={classes}
                                    icon={<VisibilityOutlinedIcon fontSize="small" />}
                                    label="Impressions"
                                    value={formatNumber(totals.impressions)}
                                />
                                <SummaryCard
                                    classes={classes}
                                    icon={<TouchAppIcon fontSize="small" />}
                                    label="Clicks"
                                    value={formatNumber(totals.clicks)}
                                />
                                <SummaryCard
                                    classes={classes}
                                    icon={<MonetizationOnOutlinedIcon fontSize="small" />}
                                    label="Cost per click"
                                    value={money(totals.cpc)}
                                />
                                <SummaryCard
                                    classes={classes}
                                    icon={<TouchAppIcon fontSize="small" />}
                                    label="Leads received"
                                    value={formatNumber(totals.leadCount)}
                                    onClick={() => openScopeLeads('received')}
                                />
                                <SummaryCard
                                    classes={classes}
                                    icon={<TouchAppIcon fontSize="small" />}
                                    label="SQL-Real"
                                    value={formatNumber(totals.sqlReal)}
                                    onClick={() => openScopeLeads('sql')}
                                />
                                <SummaryCard
                                    classes={classes}
                                    icon={<TouchAppIcon fontSize="small" />}
                                    label="Sales-TBC"
                                    value={formatNumber(totals.salesTbc)}
                                    onClick={() => openScopeLeads('tbc')}
                                />
                                <SummaryCard
                                    classes={classes}
                                    icon={<TouchAppIcon fontSize="small" />}
                                    label="Total Real"
                                    value={formatNumber(totals.totalReal)}
                                    onClick={() => openScopeLeads('real')}
                                />
                                <SummaryCard
                                    classes={classes}
                                    icon={<MonetizationOnOutlinedIcon fontSize="small" />}
                                    label="CPERP"
                                    value={money(totals.costPerReal)}
                                />
                            </div>
                            <p className={classes.footnote}>
                                Combined Meta and Google spend for the selected zone, city, and dates.
                                Meta daily spend uses synced city breakdown; Google uses stored campaign-day rows.
                                Click lead counts to open Meta or Google lead lists.
                            </p>
                            <SpendTrendChart
                                daily={visibleDaily}
                                currency={currency}
                                mixedCurrency={mixed}
                            />
                        </MarketingSpendOverviewAccordion>
                        <GoogleCampaignSpendTable
                            campaigns={visibleCampaigns}
                            records={visibleRecords}
                            from={from}
                            to={to}
                            loading={dailyLoading}
                            scopeLabel={scopeLabel}
                            onOpenLeads={openLeads}
                            resolvePrimaryCity={primaryCityForUnified}
                            hideCityTagging
                            metaCityDaily={metaCityDaily}
                            targets={targets}
                            currency={currency}
                            mixedCurrency={mixed}
                            tableTitle="City breakdown (Meta + Google)"
                            emptyMessage="No campaigns in this range for the selected filters."
                        />
                    </>
                )}
                <MetaAdsLeadDialog
                    open={Boolean(leadDialog)}
                    title={leadDialog?.title || 'Leads'}
                    query={leadDialog?.query}
                    onClose={() => setLeadDialog(null)}
                    loadLeads={leadDialog?.loader || getMetaAdsLeadDetails}
                    createdDateLabel={leadDialog?.dateLabel || 'Meta created date'}
                />
                <Dialog open={Boolean(leadPicker)} onClose={() => setLeadPicker(null)}>
                    <DialogTitle>Mixed Meta and Google leads</DialogTitle>
                    <DialogContent>Choose which channel to open.</DialogContent>
                    <DialogActions>
                        <Button onClick={() => setLeadPicker(null)}>Cancel</Button>
                        <Button color="primary" onClick={() => {
                            const p = leadPicker;
                            setLeadPicker(null);
                            openChannelLeads('meta', p.meta, p.bucket, p.label, p.day);
                        }}
                        >
                            Meta
                        </Button>
                        <Button color="primary" onClick={() => {
                            const p = leadPicker;
                            setLeadPicker(null);
                            openChannelLeads('google', p.google, p.bucket, p.label, p.day);
                        }}
                        >
                            Google
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>
        </section>
    );
}
